<script lang="ts">import { Button } from "@infinitro/button";
import { stateUi } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
/** Default: static `SeoText`, games, providers. `contained`: sports / iframe strip + card. */
export let variant = undefined;
/** Skip the outer full-container wrapper — use when already inside a padded page container (games, providers). */
export let noContainer = false;
/** Apply sports-style dark background + border-top to the outer wrapper (e.g. referral). */
export let withBackground = false;
/** Games only: drop top margin on the seo-text card. */
export let noTopMargin = false;
export let htmlContent;
export let lastModified = "";
export let showMoreButton = true;
let showMore = false;
function toggleShowMore() {
	showMore = !showMore;
}
$: useSolid = $stateUi.infoCard.infoCardStyle === "solid";
$: useSolidDark = $stateUi.infoCard.infoCardStyle === "solid-dark";
$: expanded = showMore || !showMoreButton;
$: useMomentum = $stateUi?.seoText?.style === "momentum";
$: buttonText = useMomentum ? showMore ? `${$t(Verbiage.see_less)}` : `${$t(Verbiage.see_more)}` : showMore ? `${$t(Verbiage.show_less)}` : `${$t(Verbiage.show_more)}`;
$: showMoreButtonCustomStyle = useMomentum ? "background: var(--color-accent-100); color: black; border-color: transparent;" : "";
</script>

{#if htmlContent.trim()}
	{#if variant === 'contained'}
		<div class="sports-seo-text-outer">
			<div
				class="sports-seo-text"
				class:sports-seo-text-inner-solid={useSolid}
				class:sports-seo-text-inner-solid-dark={useSolidDark}
				class:sports-seo-text-momentum={useMomentum}
				class:expanded
			>
				<div class="sports-seo-text-body">
					{@html htmlContent}
					{#if lastModified}
						<p class="last-updated">Last updated: {lastModified}</p>
					{/if}

					{#if !useMomentum}
						<div
							class="fade-out"
							class:fade-out-solid={useSolid}
							class:hidden={useSolidDark ? !expanded : expanded}
						></div>
					{/if}
				</div>
				{#if showMoreButton}
					<div class="show-more" class:show-more-momentum={useMomentum}>
						<Button
							variant="tertiary"
							className="border-1"
							minWidth={true}
							onClick={toggleShowMore}
							text={buttonText}
							styleCustom={showMoreButtonCustomStyle}
						/>
					</div>
				{/if}
			</div>
		</div>
	{:else if noContainer}
		<div
			class="seo-text"
			class:seo-text-no-top-margin={noTopMargin}
			class:seo-text-solid={useSolid}
			class:seo-text-solid-dark={useSolidDark}
			class:seo-text-momentum={useMomentum}
			class:expanded
		>
			<div class="seo-text-body">
				<div class="markdown-seo">{@html htmlContent}</div>
				{#if lastModified}
					<p class="last-updated">Last updated: {lastModified}</p>
				{/if}
				{#if !useMomentum}
					<div class="fade-out" class:fade-out-solid={useSolid} class:hidden={expanded}></div>
				{/if}
			</div>
			{#if showMoreButton}
				<div class="show-more" class:show-more-momentum={useMomentum}>
					<Button
						variant="tertiary"
						className="border-1"
						minWidth={true}
						onClick={toggleShowMore}
						text={buttonText}
						styleCustom={showMoreButtonCustomStyle}
					/>
				</div>
			{/if}
		</div>
	{:else}
		<div class="seo-section-outer" class:seo-section-outer-bg={withBackground}>
			<div class="seo-section-inner">
				<div
					class="seo-text"
					class:seo-text-no-top-margin={noTopMargin}
					class:seo-text-solid={useSolid}
					class:seo-text-momentum={useMomentum}
					class:expanded
				>
					<div class="seo-text-body">
						<div class="markdown-seo">{@html htmlContent}</div>
						{#if lastModified}
							<p class="last-updated">Last updated: {lastModified}</p>
						{/if}
						{#if !useMomentum}
							<div class="fade-out" class:fade-out-solid={useSolid} class:hidden={expanded}></div>
						{/if}
					</div>
					{#if showMoreButton}
						<div class="show-more" class:show-more-momentum={useMomentum}>
							<Button
								variant="tertiary"
								className="border-1"
								minWidth={true}
								onClick={toggleShowMore}
								text={buttonText}
								styleCustom={showMoreButtonCustomStyle}
							/>
						</div>
					{/if}
				</div>
			</div>
		</div>
	{/if}
{/if}

<style>
	/* Default markdown panel (`.seo-text` + `.markdown-seo`) */
	/* mirrors full-container + infinitro-container from @infinitro/container */
	.seo-section-outer {
		display: flex;
		height: -moz-fit-content;
		height: fit-content;
		width: 100%;
		max-width: 100vw;
		justify-content: center;
		padding-top: 1.5rem;
		padding-bottom: 1.5rem;
		padding-left: 0px;
		padding-right: 0px;
}
	@media (min-width: 1024px) {
		.seo-section-outer {
				padding: 2rem;
		}
}
	.seo-section-outer-bg {
		background-color: var(--color-background-700);
		border-top: 1px solid var(--color-neutral-900);
	}
	.seo-section-inner {
		position: relative;
		display: flex;
		height: -moz-fit-content;
		height: fit-content;
		width: 100%;
		max-width: 92vw;
		flex-direction: column;
		align-items: center;
		gap: 1rem;
}
	@media (min-width: 1280px) {
		.seo-section-inner {
				max-width: 1400px;
		}
}

	.seo-text {
		margin-top: 1rem;
		margin-bottom: 1rem;
		display: flex;
		min-height: 0px;
		width: 100%;
		flex-direction: column;
		align-items: stretch;
		border-radius: 1rem;
		padding-left: 1.5rem;
		padding-right: 1.5rem;
		padding-top: 1.5rem;
		padding-bottom: 1.5rem;
		text-align: left;
		font-family: var(--font-main);
		max-height: 350px;
		overflow: hidden;
		transition: max-height 0.3s ease;
		background-image: var(--gradient-main);
}
	.seo-text.expanded {
		max-height: none;
		overflow: visible;
	}
	.seo-text.seo-text-momentum {
		position: relative;
		isolation: isolate;
		border-radius: 0.75rem;
		padding: 1.25rem;
		background-color: rgba(255, 255, 255, 0.05);
		background-image: none;
		max-height: 327px;
}

	.seo-text.seo-text-momentum:not(.expanded)::after {
		content: '';
		position: absolute;
		bottom: 0;
		left: 0;
		width: 100%;
		height: 236px;
		pointer-events: none;
		z-index: 1;
		background: linear-gradient(
			1.04deg,
			rgba(255, 255, 255, 0.1) 0.92%,
			rgba(255, 255, 255, 0) 99.14%
		);
	}

	.seo-text.seo-text-momentum.expanded {
		max-height: none;
		overflow: visible;
	}

	.seo-text.seo-text-momentum:not(.expanded) .seo-text-body {
		flex: 1 1 0%;
}

	.seo-text-solid {
		background-image: unset;
		background-color: var(--color-background-500);
	}
	.seo-text-solid-dark {
		background-color: var(--color-background-500);
		background-image: unset;
}
	.seo-text.seo-text-momentum.seo-text-solid,
	.seo-text.seo-text-momentum.seo-text-solid-dark {
		background-color: rgba(255, 255, 255, 0.05);
		background-image: none;
	}

	.seo-text-body {
		position: relative;
		min-height: 0px;
		width: 100%;
		flex: 1 1 0%;
		overflow: hidden;
}

	.seo-text.expanded .seo-text-body {
		flex: none;
		overflow: visible;
}
	.fade-out {
		pointer-events: none;
		position: absolute;
		bottom: 0px;
		left: 0px;
		height: 100%;
		width: 100%;
		background-image: linear-gradient(to top, var(--tw-gradient-stops));
		--tw-gradient-from: var(--color-background-100) var(--tw-gradient-from-position);
		--tw-gradient-to: rgb(255 255 255 / 0) var(--tw-gradient-to-position);
		--tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to);
		--tw-gradient-to: transparent var(--tw-gradient-to-position);
}
	.fade-out-solid {
		background-image: linear-gradient(to top, var(--tw-gradient-stops));
		--tw-gradient-from: var(--color-background-500) var(--tw-gradient-from-position);
		--tw-gradient-to: rgb(255 255 255 / 0) var(--tw-gradient-to-position);
		--tw-gradient-stops: var(--tw-gradient-from), var(--tw-gradient-to);
		--tw-gradient-to: transparent var(--tw-gradient-to-position);
}
	.show-more {
		position: relative;
		z-index: 10;
		margin-top: 1rem;
		display: flex;
		width: 100%;
		flex-shrink: 0;
		justify-content: center;
}

	.show-more-momentum {
		position: absolute;
		bottom: 1.25rem;
		left: 50%;
		z-index: 20;
		margin-top: 0px;
		width: auto;
		flex-shrink: 0;
		--tw-translate-x: -50%;
		transform: translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y));
		justify-content: center;
}

	:global(.markdown-seo) {
		width: 100%;
		max-width: 100%;
		overflow-x: auto;
		text-align: left;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}
	.last-updated {
		margin-top: 1rem;
		margin-bottom: 0px;
		width: 100%;
		text-align: left;
		font-size: 0.75rem;
		line-height: 1rem;
		color: var(--color-text-300);
		font-family: var(--font-main);
}

	:global(.markdown-seo h2),
	:global(.markdown-seo h3),
	:global(.markdown-seo h4),
	:global(.markdown-seo h5),
	:global(.markdown-seo h6) {
		margin-bottom: 1.5rem;
		font-weight: 700;
		font-family: var(--font-main);
		-moz-column-span: all;
		     column-span: all;
}
	:global(.markdown-seo h2) {
		font-size: 32px;
}
	:global(.markdown-seo h3) {
		font-size: 24px;
}
	:global(.markdown-seo h4),
	:global(.markdown-seo h5),
	:global(.markdown-seo h6) {
		font-size: 20px;
}

	:global(.markdown-seo p),
	:global(.markdown-seo ul),
	:global(.markdown-seo ol) {
		margin-bottom: 1rem;
		font-size: 16px;
		line-height: 1.625;
		font-family: var(--font-main);
}

	:global(.markdown-seo.long-content) {
		-moz-columns: 2;
		     columns: 2;
		gap: 2rem;
}

	:global(.markdown-seo a) {
		color: var(--color-primary-500);
		text-decoration-line: underline;
}

	:global(.markdown-seo img),
	:global(.markdown-seo video) {
		height: auto;
		max-width: 100%;
}

	:global(.markdown-seo pre) {
		max-width: 100%;
		overflow-x: auto;
}

	:global(.markdown-seo table) {
		display: table;
		width: -moz-max-content;
		width: max-content;
		min-width: 100%;
		max-width: none;
		border-collapse: collapse;
		margin-bottom: 1rem;
		font-size: 15px;
		font-family: var(--font-main);
		border: 1px solid var(--color-neutral-700);
	}
	:global(.markdown-seo thead) {
		background-color: rgb(0 0 0 / 0.2);
}
	:global(.markdown-seo th),
	:global(.markdown-seo td) {
		border: 1px solid var(--color-neutral-700);
		padding-left: 0.75rem;
		padding-right: 0.75rem;
		padding-top: 0.5rem;
		padding-bottom: 0.5rem;
		text-align: left;
		vertical-align: top;
	}
	:global(.markdown-seo th) {
		font-weight: 700;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}
	:global(.markdown-seo td) {
		color: rgb(255 255 255 / 0.95);
}

	.seo-text.seo-text-no-top-margin {
		margin-top: 0px;
}

	.sports-seo-text-outer {
		box-sizing: border-box;
		width: 100%;
		min-width: 0px;
		max-width: 100%;
		padding: 1rem;
		background-color: var(--color-background-700);
		border-top: 1px solid var(--color-neutral-900);
}

	.sports-seo-text {
		position: relative;
		box-sizing: border-box;
		display: flex;
		min-height: 0px;
		width: 100%;
		min-width: 0px;
		max-width: 100%;
		flex-direction: column;
		align-items: stretch;
		border-radius: 0.75rem;
		padding-left: 2rem;
		padding-right: 2rem;
		padding-top: 2rem;
		padding-bottom: 2rem;
		text-align: left;
		font-family: var(--font-main);
		max-height: 350px;
		overflow: hidden;
		transition: max-height 0.3s ease;
		background-color: transparent;
		background-image: var(--gradient-main);
		border: 1px solid var(--color-neutral-900);
		overflow-wrap: break-word;
}
	.sports-seo-text.expanded {
		max-height: none;
		overflow: visible;
	}

	.sports-seo-text.sports-seo-text-momentum {
		isolation: isolate;
		background-color: rgba(255, 255, 255, 0.05);
		background-image: none;
		border-color: transparent;
		max-height: 327px;
}

	.sports-seo-text.sports-seo-text-momentum:not(.expanded)::after {
		content: '';
		position: absolute;
		bottom: 0;
		left: 0;
		width: 100%;
		height: 236px;
		pointer-events: none;
		z-index: 1;
		background: linear-gradient(
			1.04deg,
			rgba(255, 255, 255, 0.1) 0.92%,
			rgba(255, 255, 255, 0) 99.14%
		);
	}

	.sports-seo-text-inner-solid {
		background-image: var(--gradient-alternative);
	}
	.sports-seo-text-inner-solid-dark {
		background-image: unset;
		background-color: transparent;
	}

	.sports-seo-text-body {
		position: relative;
		min-height: 0px;
		width: 100%;
		flex: 1 1 0%;
		overflow: hidden;
}
	.sports-seo-text.expanded .sports-seo-text-body {
		flex: none;
		overflow: visible;
}

	.sports-seo-text .last-updated {
		width: 100%;
		text-align: left;
}

	.sports-seo-text .show-more {
		position: relative;
		z-index: 10;
		margin-top: 1rem;
		display: flex;
		width: 100%;
		flex-shrink: 0;
		justify-content: center;
}
	.sports-seo-text .show-more-momentum {
		position: absolute;
		bottom: 1.5rem;
		left: 50%;
		z-index: 20;
		margin-top: 0px;
		width: auto;
		flex-shrink: 0;
		--tw-translate-x: -50%;
		transform: translate(var(--tw-translate-x), var(--tw-translate-y)) rotate(var(--tw-rotate)) skewX(var(--tw-skew-x)) skewY(var(--tw-skew-y)) scaleX(var(--tw-scale-x)) scaleY(var(--tw-scale-y));
		justify-content: center;
}

	:global(.sports-seo-text h1),
	:global(.sports-seo-text h2) {
		margin-bottom: 1rem;
		font-size: 32px;
		font-weight: 700;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		font-family: var(--font-main);
}
	:global(.sports-seo-text h3) {
		margin-bottom: 0.75rem;
		font-size: 24px;
		font-weight: 700;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		font-family: var(--font-main);
}
	:global(.sports-seo-text p) {
		margin-bottom: 1rem;
		font-size: 1rem;
		line-height: 1.5rem;
		line-height: 1.625;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		font-family: var(--font-main);
}
	:global(.sports-seo-text a) {
		color: var(--color-primary-500);
		text-decoration-line: underline;
}

	:global(.sports-seo-text img),
	:global(.sports-seo-text video) {
		height: auto;
		max-width: 100%;
}

	:global(.sports-seo-text pre) {
		max-width: 100%;
		overflow-x: auto;
}

	:global(.sports-seo-text) {
		overflow-x: auto;
}
	:global(.sports-seo-text table) {
		display: table;
		width: -moz-max-content;
		width: max-content;
		min-width: 100%;
		border-collapse: collapse;
		margin-bottom: 1rem;
		font-size: 15px;
		font-family: var(--font-main);
		border: 1px solid var(--color-neutral-700);
	}
	:global(.sports-seo-text thead) {
		background-color: rgb(0 0 0 / 0.2);
}
	:global(.sports-seo-text th),
	:global(.sports-seo-text td) {
		border: 1px solid var(--color-neutral-700);
		padding-left: 0.75rem;
		padding-right: 0.75rem;
		padding-top: 0.5rem;
		padding-bottom: 0.5rem;
		text-align: left;
		vertical-align: top;
	}
	:global(.sports-seo-text th) {
		font-weight: 700;
}
</style>
